import {
    getPostRequestCaptureConfig,
    shouldEnableFormTracking,
} from '../../../config/SiteConfigManager';
import { logger } from '../../../utils/core/logger';
import { matchesUrlPattern } from '../../../utils/web/urlUtils';
import type { FormDetectionContext } from '../types';

// Minimum number of visible input fields to consider a page as having a "formless form"
const MIN_INPUT_FIELDS = 2;

// Selectors for submit-like buttons outside of <form> elements
const SUBMIT_BUTTON_SELECTORS = [
    'button[type="submit"]',
    'button[type="button"]',
    'button:not([type])',
    'input[type="submit"]',
    'a[role="button"]',
].join(', ');

// Keywords in button text that indicate a submit action
const SUBMIT_KEYWORDS = [
    // English
    'submit',
    'send',
    'sign up',
    'register',
    'next step',
    'continue',
    'get started',
    'create account',
    // French
    'envoyer',
    'soumettre',
    'inscription',
    'inscrire',
    'étape suivante',
    'continuer',
    'commencer',
    'créer',
    // Spanish
    'enviar',
    'registrar',
    'siguiente',
    'continuar',
    'comenzar',
    'crear cuenta',
    // German
    'absenden',
    'registrieren',
    'weiter',
    'fortfahren',
    'konto erstellen',
];

/**
 * Strategy 6: DOM Observer Detection
 *
 * Detects form submissions by watching for the email input to disappear from the DOM
 * after a submit button click. Designed for SPA multi-step forms where clicking
 * "Next Step" does a client-side transition (no backend POST) but removes the
 * form inputs from the DOM.
 *
 * DISABLED by default — enable via analytics config `form_detection_strategies.dom_observer`.
 */
export class DomObserverStrategy {
    name = 'dom_observer';
    private isActive = false;
    /**
     * EXPERIMENTAL — NOT FINISHED. When true, inputs wrapped in a submit-less <form>
     * are also treated as orphan inputs (opt-in via
     * analytics.form_detection_strategies.dom_observer_track_wrapped_forms). Covers SPAs
     * whose lead-capture inputs sit inside a <form> while the submit button is a sibling
     * outside it (e.g. PayFit welcome funnel).
     *
     * Activation works, but conversion firing is NOT reliable on multi-step SPA funnels:
     * the email-disappearance success heuristic misfires, and observeForNewButtons can bind
     * step-navigation buttons (e.g. "Continuer") → potential false positives. Prefer
     * ThankYouPageStrategy (thank_you_pages) whenever the form redirects to a success URL.
     * Do not rely on this option in production until those gaps are fixed.
     */
    private readonly trackWrappedForms: boolean;
    private trackedButtons = new WeakSet<Element>();
    private registeredListeners: { button: Element; listener: () => void }[] = [];
    private trackedEmailInput: HTMLInputElement | null = null;
    private trackedEmailScope: ParentNode | null = null;
    private pendingSubmission: {
        buttonText: string;
        inputCount: number;
        timestamp: number;
    } | null = null;
    private buttonMutationObserver: MutationObserver | null = null;
    private pollingInterval: ReturnType<typeof setInterval> | null = null;

    constructor(trackWrappedForms = false) {
        this.trackWrappedForms = trackWrappedForms;
    }

    checkAndActivate(context: FormDetectionContext): boolean {
        if (this.isActive) return true;

        if (!shouldEnableFormTracking(context.domain)) {
            const postCaptureConfig = getPostRequestCaptureConfig(context.domain);
            const isPostCapturePage = postCaptureConfig?.pagePatterns?.some((pattern) =>
                matchesUrlPattern(window.location.href, pattern)
            );
            if (!isPostCapturePage) return false;
        }

        const orphanInputs = this.findOrphanInputs();
        if (orphanInputs.length < MIN_INPUT_FIELDS) return false;

        // Some SPAs render email fields as type="text" but with autocomplete="email" or
        // inputmode="email" (e.g. PayFit welcome funnel). Detect those as email inputs.
        const emailInput = orphanInputs.find(
            (el) =>
                el instanceof HTMLInputElement &&
                (el.type === 'email' || el.autocomplete === 'email' || el.inputMode === 'email')
        ) as HTMLInputElement | undefined;
        if (!emailInput) return false;

        const submitButton = this.findSubmitButton(orphanInputs);
        if (!submitButton) return false;

        this.trackedEmailInput = emailInput;
        this.trackedEmailScope = this.findEmailReplacementScope(emailInput, submitButton);

        logger.debug('📄 DomObserverStrategy: activating', {
            orphanInputCount: orphanInputs.length,
            submitButtonText: submitButton.textContent?.trim(),
        });

        this.attachButtonListener(submitButton, orphanInputs.length, context);
        this.isActive = true;
        this.observeForNewButtons(context);

        return true;
    }

    private findOrphanInputs(): HTMLElement[] {
        const allInputs = document.querySelectorAll('input, select, textarea');
        const orphans: HTMLElement[] = [];
        allInputs.forEach((input) => {
            const el = input as HTMLElement;
            const parentForm = el.closest('form');
            // Inputs inside a <form> are normally skipped (CTAPageForm handles those).
            // With the opt-in, also accept inputs inside a "submit-less" <form> — one
            // with no internal submit control — because its real submit button lives
            // outside the form and only an observer strategy can catch it.
            if (parentForm && !(this.trackWrappedForms && this.isSubmitLessForm(parentForm))) {
                return;
            }
            if (el instanceof HTMLInputElement && (el.type === 'hidden' || el.type === 'search'))
                return;
            if (el.offsetParent === null) return;
            orphans.push(el);
        });
        return orphans;
    }

    /** A form whose submit control lives outside it: no internal button or submit input. */
    private isSubmitLessForm(form: Element): boolean {
        return form.querySelector('button, input[type="submit"], input[type="image"]') === null;
    }

    private findEmailReplacementScope(emailInput: HTMLElement, submitButton: Element): ParentNode {
        const parentForm = emailInput.closest('form');
        if (parentForm) return parentForm;

        const inputAncestors = new Set<Element>();
        let el: Element | null = emailInput;
        while (el && el !== document.body) {
            inputAncestors.add(el);
            el = el.parentElement;
        }

        el = submitButton;
        while (el && el !== document.body) {
            if (inputAncestors.has(el)) return el;
            el = el.parentElement;
        }

        return emailInput.parentElement ?? document.body;
    }

    private findSubmitButton(orphanInputs: HTMLElement[]): Element | null {
        const ancestors = new Set<Element>();
        for (const input of orphanInputs) {
            let el: Element | null = input.parentElement;
            while (el && el !== document.body) {
                ancestors.add(el);
                el = el.parentElement;
            }
        }

        const buttons = document.querySelectorAll(SUBMIT_BUTTON_SELECTORS);
        for (const button of buttons) {
            if (button.closest('form')) continue;
            if ((button as HTMLElement).offsetParent === null) continue;

            const buttonText = button.textContent?.trim().toLowerCase() || '';
            const isSubmitLike = SUBMIT_KEYWORDS.some((kw) => buttonText.includes(kw));
            if (!isSubmitLike) continue;

            let el: Element | null = button.parentElement;
            while (el && el !== document.body) {
                if (ancestors.has(el)) return button;
                el = el.parentElement;
            }
        }
        return null;
    }

    private attachButtonListener(
        button: Element,
        inputCount: number,
        context: FormDetectionContext
    ): void {
        if (this.trackedButtons.has(button)) return;
        this.trackedButtons.add(button);

        const buttonText = button.textContent?.trim() || '';

        const listener = () => {
            logger.debug('📄 DomObserverStrategy: submit button clicked', { buttonText });

            this.pendingSubmission = {
                buttonText,
                inputCount,
                timestamp: Date.now(),
            };

            this.pollForInputDisappearance(context, inputCount);
        };

        button.addEventListener('click', listener);
        this.registeredListeners.push({ button, listener });
    }

    private pollForInputDisappearance(context: FormDetectionContext, inputCount: number): void {
        const emailInput = this.trackedEmailInput;
        if (!emailInput) return;

        let checks = 0;
        const maxChecks = 10; // 10 × 500ms = 5s
        this.pollingInterval = setInterval(() => {
            checks++;
            if (!this.pendingSubmission || checks > maxChecks) {
                this.stopPolling();
                return;
            }

            // A FAILED submit (e.g. a required-field validation error) makes React
            // re-render the form: the cached email node detaches while a fresh, still
            // visible email input takes its place. That must NOT count as a submission.
            // Treat the email as gone only when no replacement email input remains visible
            // in the tracked form/scope. Other email fields elsewhere on the page (footer
            // newsletter, login, etc.) are unrelated and must not suppress this conversion.
            const cachedEmailGone =
                !document.body.contains(emailInput) || emailInput.offsetParent === null;
            const emailScope = this.trackedEmailScope ?? document;
            const hasVisibleEmailInput = Array.from(
                emailScope.querySelectorAll<HTMLInputElement>(
                    'input[type="email"], input[autocomplete="email"], input[inputmode="email"]'
                )
            ).some((el) => document.body.contains(el) && el.offsetParent !== null);
            const emailGone = cachedEmailGone && !hasVisibleEmailInput;

            const pageText = document.body.textContent?.toLowerCase() || '';
            // Word-boundary match — substring matching produced false positives:
            // 'merci' matched 'commerciale' in French privacy copy that is permanently
            // on the page, firing rw_client_form_submitted on every button click.
            const hasSuccessText = /\b(thank you|merci|success|confirmation)\b/.test(pageText);

            if (emailGone || hasSuccessText) {
                this.stopPolling();
                this.pendingSubmission = null;

                const indicators: string[] = ['dom_observer_detected'];
                if (emailGone) indicators.push('email_input_disappeared');
                if (hasSuccessText) indicators.push('success_text_detected');

                logger.debug('📄 DomObserverStrategy: form submission detected', {
                    emailGone,
                    hasSuccessText,
                    checkNumber: checks,
                });

                context.onFormSubmit({
                    formId: 'dom_observer',
                    // Method label, not a form identity — stays out of the
                    // form_id_whitelist, which could never contain it (IX-4411).
                    applyFormIdWhitelist: false,
                    pageUrl: context.currentUrl,
                    fieldCount: inputCount,
                    detectionMethod: this.name,
                    status: 'success',
                    indicators,
                });
            }
        }, 500);
    }

    private stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    private observeForNewButtons(context: FormDetectionContext): void {
        this.buttonMutationObserver = new MutationObserver(() => {
            const orphanInputs = this.findOrphanInputs();
            if (orphanInputs.length < MIN_INPUT_FIELDS) return;

            const button = this.findSubmitButton(orphanInputs);
            if (button && !this.trackedButtons.has(button)) {
                this.attachButtonListener(button, orphanInputs.length, context);
            }
        });

        this.buttonMutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    cleanup(): void {
        this.isActive = false;
        this.pendingSubmission = null;
        this.trackedEmailInput = null;
        this.trackedEmailScope = null;

        this.stopPolling();

        for (const { button, listener } of this.registeredListeners) {
            button.removeEventListener('click', listener);
        }
        this.registeredListeners = [];

        if (this.buttonMutationObserver) {
            this.buttonMutationObserver.disconnect();
            this.buttonMutationObserver = null;
        }

        logger.debug('📄 DomObserverStrategy: cleaned up');
    }
}
